"use client";

import Copyright from "../shared/Copyright";
import {
  alpha,
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Checkbox,
  styled,
  Link,
  CircularProgress,
  Typography,
} from "@mui/material";
import Logo from "../shared/Logo";
import { useEffect, useState } from "react";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GoogleIcon from "@mui/icons-material/Google";
import Terms from "../shared/Terms";
import config from "@lib/config";

const Container = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: "100vh",
  padding: "24px",
});

const Content = styled(Box)({
  width: "100%",
  maxWidth: "600px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  rowGap: "24px",
});

const Intro = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "380px",
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const Form = styled(Box)(({ theme }) => ({
  width: "100%",
  padding: "40px",
  position: "relative",
  display: "flex",
  flexDirection: "column",
  backgroundColor: alpha(theme.palette.primary.light, 0.17),
  overflow: "hidden",
  borderRadius: "6px",
}));

const Fields = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const PinkPill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "20px",
  right: "-50px",
  width: "180px",
  height: "83px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.secondary.main, 0.9),

  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const BluePill = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "70px",
  right: "-30px",
  width: "90px",
  height: "66px",
  borderRadius: "9999px",
  backgroundColor: alpha(theme.palette.primary.light, 0.7),

  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const LinkedInButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginTop: "24px",
  marginBottom: "24px",
  backgroundColor: "#fff",
  color: "#0077B5",
  border: "2px solid #0077B5",
  "&:hover": {
    backgroundColor: "#f3f9ff",
    border: "2px solid #006097",
  },
  "&:disabled": {
    backgroundColor: "#f5f5f5",
    borderColor: "#ccc",
    color: "#999",
  },
  textTransform: "none",
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  width: "100%",
  marginBottom: "24px",
  backgroundColor: "#fff",
  color: "#4285F4",
  border: "2px solid #4285F4",
  "&:hover": {
    backgroundColor: "#f0f4ff",
    border: "2px solid #3367D6",
  },
  "&:disabled": {
    backgroundColor: "#f5f5f5",
    borderColor: "#ccc",
    color: "#999",
  },
  textTransform: "none",
  display: "flex",
  gap: "12px",
  padding: "12px 16px",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "8px",
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
  },
}));

export default function Register() {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [error, setError] = useState(null);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    // Reads browser-only `window.location.search`, so it must run after mount.
    // Starting with empty `error` keeps SSR and the first client render in sync
    // (no hydration mismatch); this client-only setState is intentional.
    if (typeof window === "undefined") return;
    const msg = new URLSearchParams(window.location.search).get("msg");
    if (msg === "business-email-required") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setError("Please use a business email account");
    }
  }, []);

  const handleAcceptTerms = () => {
    setTermsAccepted(!termsAccepted);
  };

  const handlePolicyOpen = (e, url) => {
    e.preventDefault();
    setPolicy(url);
  };

  const handleClose = () => {
    setPolicy(null);
  };

  const handleRegister = (provider) => {
    if (!termsAccepted) {
      setError("Please accept the terms of service and privacy policy");
      return;
    }

    setLoadingProvider(provider);
    setError(null);

    // Full-page navigation to the external Laravel OAuth endpoint.
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination
    window.location.href = `${config.apiBaseUrl}/auth/${provider}/register?client=nextjs`;
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Container>
        <Content>
          <Box>
            <Logo />
          </Box>
          <Intro>
            <Typography variant="body1">
              Welcome to Floats!
              <br />
              Register below to set up your account.
            </Typography>
          </Intro>
          <Form>
            <PinkPill />
            <BluePill />
            <Fields>
              {error && (
                <Alert severity="error" sx={{ mb: 2, opacity: 0.8 }}>
                  {error}
                </Alert>
              )}
              <LinkedInButton
                variant="outlined"
                disabled={!!loadingProvider}
                onClick={() => handleRegister("linkedin-openid")}
              >
                {loadingProvider === "linkedin-openid" ? (
                  <CircularProgress size={24} sx={{ color: "#0077B5" }} />
                ) : (
                  <>
                    <LinkedInIcon />
                    Register with LinkedIn
                  </>
                )}
              </LinkedInButton>
              <GoogleButton
                variant="outlined"
                disabled={!!loadingProvider}
                onClick={() => handleRegister("google")}
              >
                {loadingProvider === "google" ? (
                  <CircularProgress size={24} sx={{ color: "#4285F4" }} />
                ) : (
                  <>
                    <GoogleIcon />
                    Register with Google
                  </>
                )}
              </GoogleButton>
              <FormControl>
                <FormControlLabel
                  required
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={handleAcceptTerms}
                      id="accept-terms"
                    />
                  }
                  label={
                    <label htmlFor="accept-terms">
                      I accept the{" "}
                      <Link
                        href="#"
                        onClick={(e) =>
                          handlePolicyOpen(e, "https://floats.ai/terms.html")
                        }
                      >
                        terms of service
                      </Link>{" "}
                      and{" "}
                      <Link
                        href="#"
                        onClick={(e) =>
                          handlePolicyOpen(e, "https://floats.ai/privacy.html")
                        }
                      >
                        privacy policy
                      </Link>
                      .
                    </label>
                  }
                />
              </FormControl>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{" "}
                  <Link href="/login">Sign in here</Link>
                </Typography>
              </Box>
            </Fields>
          </Form>
          <Copyright sx={{ pt: 4 }} />
        </Content>
        <Terms open={Boolean(policy)} onClose={handleClose} url={policy} />
      </Container>
    </Box>
  );
}
